import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';

export interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  author?: string;
  image?: string;
  url?: string;
  type?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
}

interface HomeSeoMeta {
  title: string;
  description: string;
  keywords: string;
  locale: string;
  htmlLang: string;
  languageLabel: string;
}

const HOME_TITLE = 'Logical Finance | Allocation Calculator & Goal Planner';

const HOME_SEO_BY_LANG: Record<string, HomeSeoMeta> = {
  cs: {
    title: HOME_TITLE,
    description:
      'Profesionální software pro finanční poradce k analýze portfolií, backtestování tisíců investičních nástrojů a komplexnímu plánování klientských cílů',
    keywords: 'backtesting portfolia, kalkulačka alokace, plánování finančních cílů, investiční nástroje',
    locale: 'cs_CZ',
    htmlLang: 'cs',
    languageLabel: 'Czech',
  },
  en: {
    title: HOME_TITLE,
    description:
      'Professional software for financial advisors to analyze portfolios, backtest thousands of investment instruments, and plan holistic client goals',
    keywords: 'portfolio backtesting, asset allocation calculator, goal-based planning, investment instruments',
    locale: 'en_US',
    htmlLang: 'en',
    languageLabel: 'English',
  },
};

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private baseUrl = 'https://www.logical.finance';
  private defaultImage = '/assets/articles/logical-logo-bile_0.png';

  constructor(
    private meta: Meta,
    private titleService: Title,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  updateHomeTags(lang: string): void {
    if (typeof window === 'undefined') {
      return;
    }

    const seo = HOME_SEO_BY_LANG[lang] ?? HOME_SEO_BY_LANG['en'];
    const ogImage = `${this.baseUrl}/assets/img/design-2026/og-seo.png`;

    this.titleService.setTitle(seo.title);
    this.meta.updateTag({ name: 'description', content: seo.description });
    this.meta.updateTag({ name: 'keywords', content: seo.keywords });
    this.meta.updateTag({ name: 'language', content: seo.languageLabel });
    this.meta.updateTag({ property: 'og:url', content: this.baseUrl });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:title', content: seo.title });
    this.meta.updateTag({ property: 'og:description', content: seo.description });
    this.meta.updateTag({ property: 'og:image', content: ogImage });
    this.meta.updateTag({ property: 'og:locale', content: seo.locale });
    this.meta.updateTag({ name: 'twitter:url', content: this.baseUrl });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: seo.title });
    this.meta.updateTag({ name: 'twitter:description', content: seo.description });
    this.meta.updateTag({ name: 'twitter:image', content: ogImage });
    this.updateCanonicalUrl(this.baseUrl);
    this.document.documentElement.lang = lang;
  }

  updateTags(config: SEOConfig): void {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return; // Skip on server-side
    }

    // Update title
    if (config.title) {
      this.titleService.setTitle(`${config.title} | Logical Finance`);
    }

    // Update or create meta tags
    if (config.description) {
      this.meta.updateTag({ name: 'description', content: config.description });
    }

    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    }

    if (config.author) {
      this.meta.updateTag({ name: 'author', content: config.author });
    }

    // Open Graph tags
    const ogUrl = config.url ? `${this.baseUrl}${config.url}` : this.baseUrl;
    const ogImage = config.image ? `${this.baseUrl}${config.image}` : `${this.baseUrl}${this.defaultImage}`;
    const ogType = config.type || 'website';

    this.meta.updateTag({ property: 'og:url', content: ogUrl });
    this.meta.updateTag({ property: 'og:type', content: ogType });
    
    if (config.title) {
      this.meta.updateTag({ property: 'og:title', content: config.title });
    }
    
    if (config.description) {
      this.meta.updateTag({ property: 'og:description', content: config.description });
    }
    
    this.meta.updateTag({ property: 'og:image', content: ogImage });

    // Additional Open Graph tags for articles
    if (config.type === 'article') {
      if (config.author) {
        this.meta.updateTag({ property: 'og:author', content: config.author });
      }
      if (config.publishedTime) {
        this.meta.updateTag({ property: 'article:published_time', content: config.publishedTime });
      }
      if (config.modifiedTime) {
        this.meta.updateTag({ property: 'article:modified_time', content: config.modifiedTime });
      }
      if (config.section) {
        this.meta.updateTag({ property: 'article:section', content: config.section });
      }
      if (config.tags && config.tags.length > 0) {
        config.tags.forEach(tag => {
          this.meta.updateTag({ property: 'article:tag', content: tag });
        });
      }
    }

    // Twitter tags
    this.meta.updateTag({ name: 'twitter:url', content: ogUrl });
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    
    if (config.title) {
      this.meta.updateTag({ name: 'twitter:title', content: config.title });
    }
    
    if (config.description) {
      this.meta.updateTag({ name: 'twitter:description', content: config.description });
    }
    
    this.meta.updateTag({ name: 'twitter:image', content: ogImage });

    // Canonical URL
    this.updateCanonicalUrl(ogUrl);

    // Add structured data for articles
    if (config.type === 'article') {
      this.addStructuredData(config);
    }
  }

  private updateCanonicalUrl(url: string): void {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return; // Skip on server-side
    }

    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    
    if (link) {
      link.setAttribute('href', url);
    } else {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      link.setAttribute('href', url);
      document.head.appendChild(link);
    }
  }

  private addStructuredData(config: SEOConfig): void {
    // Check if we're in browser environment
    if (typeof window === 'undefined') {
      return; // Skip on server-side
    }

    // Remove existing structured data
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": config.title,
      "description": config.description,
      "image": config.image ? `${this.baseUrl}${config.image}` : `${this.baseUrl}${this.defaultImage}`,
      "author": {
        "@type": "Person",
        "name": config.author
      },
      "publisher": {
        "@type": "Organization",
        "name": "Logical Finance",
        "logo": {
          "@type": "ImageObject",
          "url": `${this.baseUrl}/assets/img/logo.svg`
        }
      },
      "datePublished": config.publishedTime,
      "dateModified": config.modifiedTime || config.publishedTime,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": config.url ? `${this.baseUrl}${config.url}` : this.baseUrl
      },
      "articleSection": config.section || "Blog",
      "keywords": config.tags?.join(', ') || config.keywords
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
  }
}


